import React, { useEffect, useState } from 'react';
import { InputText, Toggle } from '@insurancemenu/imenu-components-lib';
import { useLazyQuery } from '@apollo/client';
import { HeaderUserInfo } from 'types/userService';
import { GET_USER_INFO_FOR_HEADER } from 'api/queries/userService';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import { celticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { createStyles } from './styles';

interface DateRangeProps {
  updateTableByDateRange: (dateFrom: string, dateTo: string, fullAgencyView: boolean) => void;
  showAdminToggle?: boolean;
}

const DateRange = observer(({ updateTableByDateRange, showAdminToggle = true }: DateRangeProps): JSX.Element => {
  const classes = createStyles();

  const {
    userStore: { userRoles },
  } = useRootStore();

  const currentDate = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(currentDate);
  const [dateTo, setDateTo] = useState(currentDate);
  const [isDateToCorrect, setIsDateToCorrect] = useState(true);
  const [isDateFromCorrect, setIsDateFromCorrect] = useState(true);
  const [fullAgencyView, setFullAgencyView] = useState(false);

  const onDateFromChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.value) setDateFrom(e.target.value);
  };

  const onDateToChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.value) setDateTo(e.target.value);
  };

  const getHeaderDataQuery = useLazyQuery<{
    getUserInfoForHeader: HeaderUserInfo;
  }>(GET_USER_INFO_FOR_HEADER);

  // Effects
  useEffect(() => {
    void getHeaderDataQuery[0]();
  }, [getHeaderDataQuery[0]]);

  const convertedUserRoles = userRoles.map((role) => toJS(role)).slice();

  const onToggleChange = (): void => {
    setFullAgencyView(!fullAgencyView);
  };

  const isDateRangeValid = (): boolean => {
    const dateFromTime = new Date(dateFrom).getTime();
    const dateToTime = new Date(dateTo).getTime();
    const currentTime = new Date().getTime();

    const leftCheck = dateToTime >= dateFromTime && currentTime >= dateToTime;
    setIsDateToCorrect(leftCheck);
    const rightCheck = dateToTime >= dateFromTime && currentTime >= dateFromTime;
    setIsDateFromCorrect(rightCheck);

    return leftCheck && rightCheck;
  };

  // function must be called in useEffect for avoiding React warning
  useEffect(() => {
    if (isDateRangeValid()) {
      updateTableByDateRange(dateFrom, dateTo, fullAgencyView);
    }
  }, [dateFrom, dateTo, fullAgencyView]);

  return (
    <>
      <InputText
        className={classes.dateInput}
        name="dateFrom"
        label="From"
        onChange={onDateFromChange}
        value={dateFrom}
        width="23rem"
        htmlType="date"
        currentDate={currentDate}
        validationPassed={isDateFromCorrect}
        validationMessage={isDateFromCorrect ? '' : 'Please choose correct date range'}
      />
      <InputText
        className={classes.dateInput}
        name="dateTo"
        label="To"
        onChange={onDateToChange}
        value={dateTo}
        width="23rem"
        htmlType="date"
        currentDate={currentDate}
        validationPassed={isDateToCorrect}
        validationMessage={isDateToCorrect ? '' : 'Please choose correct date range'}
      />
      {celticsRights(convertedUserRoles || []) && showAdminToggle && (
        <Toggle
          checked={fullAgencyView}
          onChange={onToggleChange}
          labelActive="Sponsor"
          labelInactive="Agency"
          className={classes.adminViewToggle}
        />
      )}
    </>
  );
});

export default DateRange;
